import React, { useState, useContext, createContext, useRef } from "react";

import styles from "./styles.module.scss";
import ProductPrices from "./ProductPrices";
import ProductDetails from "./ProductDetails";
import ProductImage from "./ProductImage";
import InventoryDetails from "./inventory_info/InventoryDetails";
import { Skeleton, Paper, Slide } from "@mui/material";

import vendors from "../constants/vendors";
import { suggestVendorConnections } from "../constants/vendors";
import { useDataContext } from "../App";
import CartErrorAlert from "./CartErrorAlert";
import getStyleNumber from "utilities/getStyleNumber";
import { LOADING_STATES } from "features/productsSlice";
import { calculateSavingsPotential } from "utilities/calculateSavingsPotential";
import { useWebsocketContext } from "authsignin/AuthedPage";
import { checkIfSaleExists } from "utilities/parseInventoryData";
import { formatPrice } from "utilities/formatPrice";
import { useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import SimilarSlide from "./SimilarSlide";
import { buildProductUrl } from "utilities/navigation";

const PricesContext = createContext();
const usePricesContext = () => useContext(PricesContext);
export { PricesContext, usePricesContext };

const RenderProductPrices = ({
  displayVendors,
  displaySuggestedVendors,
  hit,
  openInvDetails,
  setOpenInvDetails,
  setSelectedVendorCode,
  masterColor,
  setVendorProducts,
  cheapestAvailableVendor,
  showOnlySyncedVendors,
}) => {
  const { connectedVendorCodes } = useDataContext();
  const hasAtLeastOneConnection = Object.values(connectedVendorCodes).some(
    (status) => status,
  );

  if (!hasAtLeastOneConnection) {
    return (
      <Skeleton
        sx={{ bgcolor: "grey.300" }}
        variant="rectangular"
        width={360}
        height={198}
      />
    );
  }

  return (
    <ProductPrices
      displayVendors={displayVendors}
      displaySuggestedVendors={displaySuggestedVendors}
      hit={hit}
      openInvDetails={openInvDetails}
      setOpenInvDetails={setOpenInvDetails}
      setSelectedVendorCode={setSelectedVendorCode}
      masterColor={masterColor}
      setVendorProducts={setVendorProducts}
      cheapestAvailableVendor={cheapestAvailableVendor}
      showOnlySyncedVendors={showOnlySyncedVendors}
    />
  );
};

const Product = React.memo(function Product({ hit }) {
  const [selectedColor, setSelectedColor] = useState({ name: "" });
  const [openInvDetails, setOpenInvDetails] = useState(hit.singleHit);
  const [openSimilarSlide, setOpenSimilarSlide] = useState(false);
  const [showCartAlert, setShowCartAlert] = useState(false);
  const [cartAlertContent, setCartAlertContent] = useState();
  const [selectedVendorCode, setSelectedVendorCode] = useState("");
  const { connectedVendorCodes, setSearchSession } = useDataContext();
  const inventorySlideRef = useRef(null);
  const productCardRef = useRef(null);
  const similarSlideRef = useRef(null);
  const [loadingTableData, setLoadingTableData] = useState(false);
  const [vendorProducts, setVendorProducts] = useState({});
  const { sendJsonMessage } = useWebsocketContext();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const location = useLocation();

  const selectedProductKey =
    (hit.dgi_style_sku || hit.id) + selectedVendorCode + selectedColor.name;
  const vendorProduct = vendorProducts[selectedProductKey];
  const showOnlySyncedVendors = useSelector(
    (state) => state.user?.user?.show_only_synced_vendors,
  );
  const hasColors = Boolean(hit.colors);

  // New function to handle  slider toggling
  const toggleSlider = (slider) => {
    if (slider === "inventory") {
      setOpenInvDetails((prev) => {
        if (!prev) setOpenSimilarSlide(false); // Close the similar slide if opening inventory
        return !prev;
      });
    } else if (slider === "similar") {
      setOpenSimilarSlide((prev) => {
        if (!prev) setOpenInvDetails(false); // Close the inventory slide if opening similar
        return !prev;
      });
    }
  };

  const sortVendorsByPrice = (vendorA, vendorB) => {
    const dataCodeA = vendorA["code"];
    const dataCodeB = vendorB["code"];
    let priceA, priceB;
    const keyA = hit.id + dataCodeA + selectedColor.name;
    const keyB = hit.id + dataCodeB + selectedColor.name;
    if (vendorProducts && vendorProducts[keyA]) {
      const vendorAData = vendorProducts[keyA];
      if (vendorAData.data) {
        priceA = vendorAData.data?.product_price;
      }
    }
    if (vendorProducts && vendorProducts[keyB]) {
      const vendorBData = vendorProducts[keyB];
      if (vendorBData.data) {
        priceB = vendorBData.data?.product_price;
      }
    }

    if (priceA === "Color N/A" || !priceA) {
      priceA = Infinity;
    }
    if (priceB === "Color N/A" || !priceB) {
      priceB = Infinity;
    }

    if (priceA < priceB) {
      return -1;
    }
    if (priceA > priceB) {
      return 1;
    }
    return 0;
  };

  const checkAllPricesRendered = (displayVendors) => {
    if (!displayVendors || displayVendors.length === 0) {
      return false;
    }
    if (!vendorProducts) {
      return false;
    }
    for (const vendor of displayVendors) {
      const key = hit.id + vendor.code + selectedColor.name;
      const vendorData = vendorProducts[key];
      if (!vendorData) {
        return false;
      }

      if (vendorData?.loading === LOADING_STATES.LOADING_STATUS) {
        return false;
      }
    }

    return true;
  };

  const getDisplayVendors = () => {
    if (hit.direct) {
      return [{ code: hit.direct_vendor }];
    }
    return Object.entries(vendors)
      .filter((vendor) => {
        const [dataKey, vendorFields] = vendor;
        const styleField = vendorFields["style_field"];
        if (connectedVendorCodes[dataKey] && hit && hit[styleField]) {
          return true;
        } else {
          return false;
        }
      })
      .map((vendor) => vendor[1])
      .sort(sortVendorsByPrice);
  };

  const displayVendors = getDisplayVendors();

  const displaySuggestedVendors = Object.entries(vendors)
    .filter((vendor) => {
      const [dataKey, vendorFields] = vendor;
      if (!suggestVendorConnections.has(vendorFields["code"])) {
        return false;
      }
      const styleField = vendorFields["style_field"];
      if (!connectedVendorCodes[dataKey] && hit && hit[styleField]) {
        return true;
      } else {
        return false;
      }
    })
    .map((vendor) => vendor[1]);
  const suggestedAndConnectedVendors = displayVendors.concat(
    displaySuggestedVendors,
  );
  const allRelevantVendors = showOnlySyncedVendors
    ? displayVendors
    : suggestedAndConnectedVendors;

  const vendorDataCodes = allRelevantVendors.map((v) => v.code);
  const vendorDataCodesOrdered = checkAllPricesRendered(displayVendors);
  const smExclusive =
    hit?.distributors?.length === 1 && hit?.distributors[0] === "Sanmar";

  // in the case that the user didn't already select a tab,
  // we continually assign the cheapest vendor tab until all prices come in
  const firstVendorCode =
    vendorDataCodes.length > 0 ? vendorDataCodes[0] : null;
  const firstKey =
    (hit.dgi_style_sku || hit.id) + firstVendorCode + selectedColor.name;
  const isVendorAvailable =
    vendorProducts &&
    vendorProducts[firstKey] &&
    vendorProducts[firstKey]?.data?.product_price;
  const cheapestAvailableVendor = isVendorAvailable
    ? { firstKey: firstVendorCode, areKeysOrdered: vendorDataCodesOrdered }
    : null;

  if (hit === undefined) {
    return;
  }

  const calculateSavingsFcn = (quantity, selectedVendorPrice) => {
    return calculateSavingsPotential(
      quantity,
      selectedVendorPrice,
      vendorProducts,
    );
  };

  const displayStyleNumber = hit?.master_style || getStyleNumber(hit);
  const pricesContext = {
    setSelectedColor,
    selectedColor,
  };

  const renderCartAlert = showCartAlert && (
    <CartErrorAlert alertContent={cartAlertContent} />
  );

  const vendorsBestPrices = {};
  for (const vendorCode of vendorDataCodes) {
    const vendorProductKey = hit.id + vendorCode + selectedColor?.name;
    const vendorProduct = vendorProducts[vendorProductKey];
    const vendorSizedPricingData =
      vendorProduct?.["data"]?.["sized_pricing_data"];
    const saleExists = checkIfSaleExists(vendorSizedPricingData);
    const formattedDollarAmount = formatPrice(
      vendorProduct?.["data"]?.["product_price"],
    );
    vendorsBestPrices[vendorCode] = {
      productPrice: formattedDollarAmount,
      saleExists,
    };
  }

  const navigateToProductPage = (e) => {
    e.stopPropagation();
    const dgiStyle = hit["dgi_style_sku"] || hit["id"];
    setSearchSession((currentSearchSession) => ({
      ...currentSearchSession,
      prev_path: "shop",
    }));
    const productUrl = buildProductUrl(dgiStyle, selectedColor.name);
    navigate(productUrl);
  };

  const onSimilarClick = (event) => {
    event.stopPropagation();
    toggleSlider("similar");
  };

  const similarClickHandle = (e) => {
    // track clicks for most basic analytic on button
    if (!openSimilarSlide) {
      posthog?.capture("fetch_similars_prices", {
        original_vendor_style: hit.master_style,
        original_dgi_style: hit.id,
        original_brand: hit.master_brand,
        original_color: selectedColor?.name,
        source: location.pathname,
      });
    }
    onSimilarClick(e);
  }

  return (
    <PricesContext.Provider value={pricesContext}>
      <div className={styles.productContainer} ref={productCardRef}>
        <div
          className={
            openInvDetails ? styles.expandedContainer : styles.previewContainer
          }
        >
          <div className="flex w-full flex-wrap md:flex-nowrap h-full">
            <div
              className={styles.productInformation}
              style={{ cursor: "pointer" }}
              onClick={navigateToProductPage}
            >
              <ProductImage
                dgiStyle={hit.direct ? hit["dgi_style_sku"] : hit["id"]}
                smExclusive={smExclusive}
                hasColors={hasColors}
              />
              <ProductDetails
                displayStyleNumber={displayStyleNumber}
                hit={hit}
                sendJsonMessage={sendJsonMessage}
                similarClickHandle={similarClickHandle}
              />
            </div>
            <RenderProductPrices
              displayVendors={displayVendors}
              displaySuggestedVendors={displaySuggestedVendors}
              hit={hit}
              openInvDetails={openInvDetails}
              setOpenInvDetails={(open) => {
                toggleSlider("inventory");
                setOpenInvDetails(open);
              }}
              setSelectedVendorCode={setSelectedVendorCode}
              masterColor={selectedColor.name}
              setVendorProducts={setVendorProducts}
              cheapestAvailableVendor={cheapestAvailableVendor}
              showOnlySyncedVendors={showOnlySyncedVendors}
            />
          </div>
        </div>
        <div
          className={styles.slideContainer}
          data-open={openInvDetails}
          ref={inventorySlideRef}
        >
          <Slide
            in={openInvDetails}
            direction="down"
            timeout={200}
            className={styles.detailsSlide}
            container={inventorySlideRef.current}
            anchor="up"
          >
            <Paper
              sx={{
                borderTopLeftRadius: "0px",
                borderTopRightRadius: "0px",
                boxShadow: "none",
                height: "100%",
              }}
            >
              {renderCartAlert}
              <InventoryDetails
                hit={hit}
                openInvDetails={openInvDetails}
                vendorDataCodes={vendorDataCodes}
                productColors={hit.colors}
                selectedVendorCode={selectedVendorCode}
                setSelectedVendorCode={setSelectedVendorCode}
                showCartAlert={showCartAlert}
                setShowCartAlert={setShowCartAlert}
                setCartAlertContent={setCartAlertContent}
                selectedColor={selectedColor}
                applyPadding={true}
                loadingTableData={loadingTableData}
                setLoadingTableData={setLoadingTableData}
                cheapestAvailableVendor={cheapestAvailableVendor}
                vendorProduct={vendorProduct}
                calculateSavingsFcn={calculateSavingsFcn}
                vendorsBestPrices={vendorsBestPrices}
              />
            </Paper>
          </Slide>
        </div>
        <div
          className={styles.similarSlideContainer}
          data-open={openSimilarSlide}
          ref={similarSlideRef}
        >
          <Slide
            in={openSimilarSlide}
            direction="down"
            timeout={200}
            className={styles.detailsSlide}
            container={similarSlideRef.current}
            anchor="up"
          >
            <Paper
              sx={{
                borderTopLeftRadius: "0px",
                borderTopRightRadius: "0px",
                boxShadow: "none",
                height: "auto",
              }}
            >
              <SimilarSlide
                open={openSimilarSlide}
                containerRef={similarSlideRef}
                hit={hit}
                selectedColor={selectedColor}
              />
            </Paper>
          </Slide>
        </div>
      </div>
    </PricesContext.Provider>
  );
});

export default Product;
